import Input from "@Components/Input";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

import styles from "./Dropdown.module.scss";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import useClickOutside from "hooks/useOutsideClick";
import DropdownIcon from "@Icons/DropdownIcon";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

const SearchableDropdown = ({
  value,
  label,
  placeholder,
  handleOptionClick,
  clearable,
  onFocus,
  options = {},
  showOptions,
  handleClear,
  showErrorMessage,
  error,
  required,
  disabled,
  handleOutsideClick,
  dropdownContainerClassName,
  dataTestId,
  showPlaceholderAndLabel = false,
  handleOnChange = null,
}: any) => {
  const dropdownRef = useRef(null);
  const [inpValue, setInpValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const { t } = useTranslationHook();

  const handleChange = (val: any) => {
    setInpValue(val);
    const formattedOptions = Object.keys(options).reduce((acc: any, opt) => {
      if (opt.toLowerCase().includes(val.toLowerCase())) {
        acc[opt] = options[opt];
      }
      return acc;
    }, {});
    setFilteredOptions(formattedOptions);
  };

  const handlKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && filteredOptions && Object.keys(filteredOptions).length > 0) {
      e.preventDefault();
      setInpValue(Object.keys(filteredOptions)[0]);
      handleOptionClick(Object.keys(filteredOptions)[0]);
      setTimeout(() => handleOutsideClick(), 200);
    }
  };

  useEffect(() => setInpValue(value), [value]);

  useClickOutside(dropdownRef, () => {
    const isSelectedValuePresent = Object.keys(options).find((opt: any) => opt === value);
    setFilteredOptions(options);
    if (!isSelectedValuePresent && showOptions === label) {
      setInpValue("");
      handleOptionClick("");
    }
    if (inpValue && inpValue !== value && showOptions === label) {
      setInpValue(value);
      handleOptionClick(value);
    }
    if (typeof handleOutsideClick !== undefined && showOptions === label) handleOutsideClick();
  });

  useEffect(() => {
    setFilteredOptions(options);
  }, [JSON.stringify(options)]);

  return (
    <div
      className={formatClassNames(styles.dropdownContainer, dropdownContainerClassName)}
      ref={dropdownRef}
    >
      <Input
        dataTestId={dataTestId}
        type="dropdown"
        label={t(label)}
        placeholder={placeholder}
        value={inpValue ? options[inpValue] : ""}
        onFocus={() => {
          onFocus();
          setFilteredOptions(options);
        }}
        clearable={clearable}
        handleClear={() => {
          setInpValue("");
          handleClear();
        }}
        onKeyDown={handlKeyDown}
        showErrorMessage={showErrorMessage}
        error={error}
        required={required}
        disabled={disabled}
        onChange={handleOnChange || handleChange}
        containerClassName={styles.dropdownInput}
        rightIcon={inpValue === "" && <DropdownIcon size={15} />}
        showPlaceholderAndLabel={showPlaceholderAndLabel}
      />
      {showOptions === label && Object.keys(filteredOptions).length > 0 && (
        <div className={styles.options_container} role="none">
          {Object.keys(filteredOptions).map((opt: any, idx: number) => (
            <div
              className={formatClassNames(styles.option, inpValue === opt ? styles.selected : "")}
              key={`${opt.label}-${idx}`}
              role="none"
              onClick={() => {
                handleOptionClick(opt);
                setTimeout(() => handleOutsideClick(), 200);
              }}
            >
              <div>{filteredOptions[opt]}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(SearchableDropdown, {
  componentName: "SearchableDropdown-BookForm",
  showForChina: true,
});
